import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useCurrentProject } from '@material-bank/projects-utils';
import {
  AnalyticsEventLocation,
  getAnalyticsItemCategoriesByTaxonomy,
} from '@mb/lib';
import { Box } from '@mui/system';
import uniq from 'lodash/uniq';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { CloseButton } from './CloseButton';
import * as Styled from './ImagesContainer.styled';
import { ImagesContainerPlaceholder } from './ImagesContainerPlaceholder';
import { useFullscreen } from './useFullscreen';
import { formatImageSource } from '../../../../pages/ProductSearch/helpers';
import { definedTranslation } from '../../../../pages/ProductSearch/i18n';
import { sendAnalytics } from '../../api/events';
import { handleAddToBoards } from '../../helpers';
import { pushItemImageSelectedDataLayer } from '../../helpers/analytics';
import { sanitizeFileName } from '../../helpers/sanitizeFileName';
import { selectedChildMaterialSelector } from '../../redux/selectors/materialsSelector';
import { ProductDetails } from '../../types/product';
import { DownloadButtonComponent } from '../DownloadButtonComponent';
import ImageSlider from '../ImageSlider/ImageSlider';
import TooltipCard from '../TooltipCard/TooltipCard';

interface ImagesContainerProps {
  mainImage: string;
  isLoading: boolean;
  images: string[];
  activeProduct: ProductDetails;
  itemListId: string;
  itemListName: string;
  clickedProductGridIndex?: number;
  shouldHideTooltip?: boolean;
  modalType:
    | AnalyticsEventLocation.MINI_PDP
    | AnalyticsEventLocation.QUICK_VIEW;
  dataLayerAnalytics?: {
    listId: string;
    listName: string;
    eventLocation: string;
  };
  onCloseTooltip: () => void;
}

export function ImagesContainer({
  mainImage,
  isLoading,
  images = [],
  activeProduct,
  itemListId,
  itemListName,
  clickedProductGridIndex,
  shouldHideTooltip,
  modalType,
  dataLayerAnalytics,
  onCloseTooltip,
}: ImagesContainerProps) {
  const intl = useIntl();
  const { currentProject } = useCurrentProject();

  const selectedMaterial = useSelector(selectedChildMaterialSelector);
  const [currentImage, setCurrentImage] = useState(mainImage);
  const { isFullScreen, handleFullScreen } = useFullscreen(activeProduct);
  const parentRef = useRef<HTMLDivElement | null>(null);
  const stickyRef = useRef<HTMLDivElement | null>(null);

  const labels = {
    addToBoardLabel: intl?.formatMessage(
      definedTranslation.card.saveButtonLabel,
    ),
  };

  useEffect(() => {
    setCurrentImage(mainImage);
  }, [mainImage]);

  const imageList = useMemo(() => {
    const imagesRaw = [mainImage, ...(images || [])]
      .map((item) => item?.split('?')?.[0])
      .filter((img) => img && !img.includes('no_selection'));
    return uniq(imagesRaw);
  }, [mainImage, images]);

  const handleFavoritesClick = async (event: React.SyntheticEvent) => {
    const product = activeProduct?.originalMaterial;

    event.preventDefault();
    handleAddToBoards(
      product,
      dataLayerAnalytics?.listId ?? itemListId,
      dataLayerAnalytics?.listName ?? itemListName,
      clickedProductGridIndex,
      dataLayerAnalytics?.eventLocation ?? modalType,
      currentProject,
    );

    const stringIds = [String(product?.entityId)];
    const value = { value: 'minipdp' };
    sendAnalytics({ id: stringIds, type: 'favorite', ...value });
  };

  const handleCurrentImage = (image: string) => {
    pushItemImageSelectedDataLayer([
      {
        item_id: activeProduct?.originalMaterial?.entityId,
        item_name: activeProduct?.originalMaterial?.name,
        item_brand: activeProduct?.originalMaterial?.manufacturer,
        ...getAnalyticsItemCategoriesByTaxonomy(
          activeProduct?.originalMaterial?.taxonomy,
        ),
        index: 1,
        price: 0,
        quantity: 1,
        item_list_id: 'quick_view',
        item_list_name: 'Quick View',
        item_variant: activeProduct?.originalMaterial?.name,
      },
    ]);
    if (image === currentImage) {
      return;
    }
    const biggerImage = formatImageSource(image, 1200, false, 80);
    setCurrentImage(biggerImage.src);
  };

  if (isLoading) {
    return <ImagesContainerPlaceholder />;
  }

  const downloadFileName = `${sanitizeFileName(
    activeProduct?.originalMaterial?.name,
  )}${
    activeProduct?.originalMaterial?.title
      ? `--${sanitizeFileName(activeProduct?.originalMaterial?.title)}`
      : ''
  }`;

  const shouldShowTooltip = selectedMaterial != null && !shouldHideTooltip;

  return (
    <Styled.ImageOuterWrapper isFullScreen={isFullScreen} ref={parentRef}>
      <CloseButton
        isFullScreen={isFullScreen}
        currentImage={currentImage}
        downloadFileName={downloadFileName}
        onClose={handleFullScreen}
      />
      <div
        ref={stickyRef}
        style={{
          position: 'sticky',
          top: 0,
          ...(shouldShowTooltip ? { width: '80%' } : {}),
        }}
      >
        {shouldShowTooltip ? (
          <TooltipCard
            material={selectedMaterial}
            onCloseTooltip={onCloseTooltip}
            intl={intl}
          />
        ) : (
          <div className="flex flex-col items-end gap-3">
            {!isFullScreen && (
              <Styled.AddToBoardButton
                color="secondary"
                size="medium"
                variant="contained"
                onClick={handleFavoritesClick}
              >
                {labels.addToBoardLabel}
              </Styled.AddToBoardButton>
            )}
            <Styled.ImageWrapper className="flex justify-center items-center relative">
              <Box
                position="relative"
                maxHeight="100%"
                display="flex"
                justifyContent="center"
                alignItems="center"
                minWidth="430px"
                minHeight="430px"
              >
                {!isFullScreen && (
                  <Styled.FullScreenButton
                    dense
                    iconName="full-screen"
                    color="secondary"
                    onClick={handleFullScreen}
                  />
                )}
                {!isFullScreen ? (
                  <DownloadButtonComponent
                    imageUrl={currentImage}
                    productName={downloadFileName}
                  />
                ) : null}
                <img
                  src={currentImage}
                  style={imageStyle}
                  onClick={handleFullScreen}
                />
              </Box>
            </Styled.ImageWrapper>
            <ImageSlider
              activeProduct={activeProduct}
              imageList={imageList}
              currentImage={currentImage}
              onImageSelect={handleCurrentImage}
            />
          </div>
        )}
      </div>
    </Styled.ImageOuterWrapper>
  );
}

const imageStyle: React.CSSProperties = {
  maxWidth: '430px',
  maxHeight: '430px',
  width: 'auto',
  height: 'auto',
  objectFit: 'contain' as const,
  borderRadius: '20px',
  filter: 'brightness(0.95)',
  display: 'block',
};
